import { useState } from "react"
import styled from "styled-components"

import {
  Button,
  colors,
  spacing,
  Text,
  TextArea,
} from "@minuthq/meatball-ui-react"

import { HomesDropdown } from "src/components/Dropdown/HomesDropdown"
import { MessageProps } from "src/components/GuestInbox/GuestChat/Message"
import { THome } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePostPropertyContextTestMessage } from "src/data/propertyContext/propertyContextQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { ChatPanel } from "src/ui/ChatPanel/ChatPanel"
import { MCard } from "src/ui/MCard/MCard"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { ErrorService } from "src/utils/ErrorService"

export function AiAssistantPreview() {
  const { tPlaceholder } = useTranslate()

  const { org } = useOrganization()
  const user = useGetUser()

  const [messages, setMessages] = useState<MessageProps[]>([])

  const [inputMessage, setInputMessage] = useState("")
  const [selectedHome, setSelectedHome] = useState<THome | null>(null)

  const postPropertyContextTestMessage = usePostPropertyContextTestMessage()

  function addMessage(message: Omit<MessageProps, "id">) {
    const newMessage = {
      id: crypto.randomUUID(),
      ...message,
    }

    setMessages((prev) => [...prev, newMessage])

    return newMessage.id
  }

  function removeMessage(id: string) {
    setMessages((prev) => prev.filter((message) => message.id !== id))
  }

  function sendTestMessage() {
    if (!selectedHome || !inputMessage) {
      return
    }

    const newMessageId = addMessage({
      content: inputMessage,
      senderType: "self",
      sentAt: new Date().toISOString(),
      source: "Minut",
      senderName: user.fullname,
    })

    postPropertyContextTestMessage.mutate(
      {
        path: {
          organization_id: org.id,
        },
        body: {
          content: inputMessage,
          home_id: selectedHome.home_id,
          messages: messages.map((message) => ({
            role: message.senderType === "self" ? "user" : "assistant",
            content: message.content,
          })),
        },
      },
      {
        onSuccess: (data) => {
          addMessage({
            content: data.content,
            senderType: "other",
            sentAt: new Date().toISOString(),
            source: tPlaceholder("Autopilot"),
            badges: data.is_escalation_message
              ? [
                  {
                    color: "warning",
                    label: tPlaceholder("Escalation"),
                    size: "small",
                    border: true,
                  },
                ]
              : [],
            senderName: "Minut",
          })
        },
        onError: () => {
          ErrorService.captureException(
            "Error sending test message for property contexts"
          )

          removeMessage(newMessageId)
        },
      }
    )

    setInputMessage("")
  }

  function reset() {
    setMessages([])
    setInputMessage("")
    setSelectedHome(null)
    postPropertyContextTestMessage.reset()
  }

  return (
    <StyledCard boxShadow={false} border padding={0}>
      <Header>
        <Text variant="subtitle">{tPlaceholder("Autopilot preview")}</Text>

        <Button variant="minimal" size="small" color="neutral" onClick={reset}>
          {tPlaceholder("Reset")}
        </Button>
      </Header>

      <ChatPanel
        messages={messages}
        onLoadMore={() => {}}
        isLoadingMore={false}
        renderInput={() => (
          <InputWrapper>
            <TextArea
              placeholder={tPlaceholder("Type your message here...")}
              value={inputMessage}
              onChange={(value) => setInputMessage(value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  sendTestMessage()
                }
              }}
              minRows={3}
            />

            <HomesDropdown
              label={
                !selectedHome
                  ? tPlaceholder("Select a unit")
                  : (selectedHome.name ?? "")
              }
              selectedHome={selectedHome ?? null}
              onSelect={setSelectedHome}
            />

            <MTooltip
              title={!selectedHome ? tPlaceholder("Select a unit") : undefined}
            >
              <Button
                onClick={sendTestMessage}
                color="neutral"
                size="small"
                loading={postPropertyContextTestMessage.isPending}
                disabled={!selectedHome || !inputMessage}
                fullWidth
              >
                {tPlaceholder("Send")}
              </Button>
            </MTooltip>
          </InputWrapper>
        )}
        emptyContent={
          <div>
            <Text color="secondary" textAlign="center">
              {tPlaceholder("Let’s try it!")}
            </Text>
            <Text color="secondary" textAlign="center">
              {tPlaceholder(
                "Ask Autopilot a question that your customer might ask to see the response."
              )}
            </Text>
          </div>
        }
      />
    </StyledCard>
  )
}

const StyledCard = styled(MCard)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.M};
`

const InputWrapper = styled.div`
  display: grid;
  gap: ${spacing.S};
  border-top: 1px solid ${colors.divider};
  padding: ${spacing.M};
`
